/**
 * Gate: a togglable solid block driven by switches sharing its `id`.
 *
 * When "closed" the body collides like a regular platform. When "open" it
 * becomes a sensor (passes through) and its view fades. State is set
 * externally by GameScene each tick from the union of switch overlaps /
 * latched presses for the gate's id.
 *
 * `inverted: true` means the gate STARTS open and closes when its switches
 * fire — handy for "hold the switch to keep the bridge in place" puzzles.
 *
 * Determinism: state changes are a pure function of physics overlap (which
 * is deterministic) so replays reproduce exactly.
 */

import Matter from 'matter-js';
import { Graphics } from 'pixi.js';
import { switchTintForId } from './Switch.js';

export interface GateOptions {
  x: number;
  y: number;
  w: number;
  h: number;
  /** Rotation in degrees around the rect's centre. Defaults to 0. */
  angle?: number;
  /** Circuit id; switches with the same id drive this gate. */
  id: string;
  /** If true, gate starts OPEN and closes when its switches activate. */
  inverted?: boolean;
}

export class Gate {
  readonly body: Matter.Body;
  readonly view: Graphics;
  readonly id: string;
  readonly inverted: boolean;
  readonly tint: number;

  /** Authoring rect (used by the editor). */
  readonly rect: { x: number; y: number; w: number; h: number; angle?: number };

  /** Logical "closed" state (solid). */
  private closed: boolean;
  /** Eased visual weight for fade between open/closed. */
  private fade = 1;

  constructor(opts: GateOptions) {
    this.id = opts.id;
    this.inverted = !!opts.inverted;
    this.tint = switchTintForId(opts.id);
    this.rect = { x: opts.x, y: opts.y, w: opts.w, h: opts.h, angle: opts.angle ?? 0 };

    const cx = opts.x + opts.w / 2;
    const cy = opts.y + opts.h / 2;
    const angleRad = ((opts.angle ?? 0) * Math.PI) / 180;

    this.body = Matter.Bodies.rectangle(cx, cy, opts.w, opts.h, {
      isStatic: true,
      friction: 0.05,
      frictionStatic: 0.05,
      restitution: 0.35,
      angle: angleRad,
      label: 'gate',
    });

    // Initial state: closed unless inverted.
    this.closed = !this.inverted;
    this.body.isSensor = !this.closed;
    this.fade = this.closed ? 1 : 0;

    const g = new Graphics();
    this.view = g;
    this.redraw();
  }

  /** Drive the gate state. `closed=true` → solid; false → pass-through. */
  setClosed(closed: boolean): void {
    if (this.closed === closed) return;
    this.closed = closed;
    this.body.isSensor = !closed;
  }

  /** Per-frame visual update; cheap fade between solid + ghost states. */
  render(): void {
    const target = this.closed ? 1 : 0;
    this.fade += (target - this.fade) * 0.18;
    this.redraw();
  }

  private redraw(): void {
    const g = this.view;
    g.clear();
    const { x, y, w, h, angle } = this.rect;
    const cx = x + w / 2, cy = y + h / 2;
    const rad = ((angle ?? 0) * Math.PI) / 180;
    const cos = Math.cos(rad), sin = Math.sin(rad);
    const corners: Array<[number, number]> = [
      [-w / 2, -h / 2], [ w / 2, -h / 2],
      [ w / 2,  h / 2], [-w / 2,  h / 2],
    ].map(([lx, ly]) => [cx + lx * cos - ly * sin, cy + lx * sin + ly * cos]);

    // Solid fill weight follows fade; open state shows just a thin outline
    // tinted by the circuit colour so the player still sees the slot.
    const solidAlpha = 0.85 * this.fade + 0.05;
    const strokeAlpha = 0.85;
    g.moveTo(corners[0]![0], corners[0]![1]);
    for (let i = 1; i < corners.length; i++) g.lineTo(corners[i]![0], corners[i]![1]);
    g.closePath();
    g.fill({ color: this.tint, alpha: solidAlpha });
    g.stroke({ color: this.tint, width: this.closed ? 2 : 1, alpha: strokeAlpha });

    // Hatch cue along the diagonal when open, so it reads as "passable".
    if (this.fade < 0.7) {
      const step = 14;
      const ax = corners[0]![0], ay = corners[0]![1];
      const bx = corners[1]![0], by = corners[1]![1];
      const dx = corners[3]![0] - ax, dy = corners[3]![1] - ay;
      const along = Math.hypot(bx - ax, by - ay);
      const ux = (bx - ax) / along, uy = (by - ay) / along;
      for (let t = 0; t < along; t += step) {
        const sx = ax + ux * t, sy = ay + uy * t;
        g.moveTo(sx, sy).lineTo(sx + dx * 0.6, sy + dy * 0.6)
          .stroke({ color: this.tint, width: 1, alpha: (1 - this.fade) * 0.5 });
      }
    }
  }
}
